import { supabase } from '../supabaseClient';
import {
    UUID,
    alsAntwort,
    alsObjekt,
    alsPlotFehler,
    fehler,
    leerZuNull,
    rufe,
    text,
    textOderNull,
    zahl,
    zahlOderNull,
} from './plotAuftraege';
import type { Antwort, Zeile } from './plotAuftraege';
import { PlotFehler } from './plotTypen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLOTTER-PAPIERROLLE, DRUCKLISTE, DRUCK NACHTRAGEN (SQL Nr. 169/170)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Rolle gilt je Firma. Ein Admin erfasst die eingelegte Rolle
 * (`plot_rollen`, art 'neu' = volle Länge, 'korrektur' = gemessene Restlänge);
 * ab jeder solchen Zeile wird neu gezählt. Gezählt wird NUR im SQL
 * (`plot_rolle_rechnen`) — der Client rechnet allein den Bedarf des neuen
 * Drucks (`plotBedarfMm`) und vergleicht ihn mit `frei_mm` vom Server
 * (`rollenUrteil`). Die Sperre selbst sitzt im BEFORE-INSERT-Trigger auf
 * `plot_auftraege`; das Urteil hier zeigt sie nur vorher an.
 *
 * Drucke, die nicht über die App liefen (alter Plan direkt vom Rechner),
 * werden von Hand nachgetragen (`plot_druck_nachtraege`) — sie zählen gegen
 * die Rolle und stehen in der Druckliste des Admins.
 *
 * Fehler wie in plotAuftraege.ts: immer `PlotFehler` mit deutschem Satz;
 * fehlt das SQL (Nr. 169/170 offen), liefern die Lesefunktionen `null` bzw.
 * eine leere Liste — dann wird nie gesperrt.
 */

// ─── Grenzwerte (gleichlautend in supabase/plot_rollen*.sql) ────────────────

export const PLOT_ROLLE_STANDARD_MM = 20_000;
export const PLOT_ROLLE_MIN_NEU_MM = 1_000;
export const PLOT_ROLLE_MAX_MM = 200_000;
export const PLOT_NACHTRAG_MIN_MM = 100;
export const PLOT_NACHTRAG_MAX_MM = 6_000;
export const PLOT_NACHTRAG_MAX_KOPIEN = 10;
export const PLOT_NACHTRAG_MAX_TAGE_ZURUECK = 366;
/** Warnschwelle: Rest nach diesem Druck unter 3 m → gelber Hinweis. */
export const PLOT_ROLLE_KNAPP_MM = 3_000;

const TITEL_MAX = 200;
const BEMERKUNG_MAX = 500;

// ─── Typen ──────────────────────────────────────────────────────────────────

export type PlotRolleArt = 'neu' | 'korrektur';

export interface PlotRolle {
    id: string;
    art: PlotRolleArt;
    laenge_mm: number;
    eingelegt_am: string;
    erstellt_von_name: string | null;
    bemerkung: string | null;
}

/**
 * Antwort von `plot_rolle_stand()`. Ohne Rolle: `rolle`, `rest_mm` und
 * `frei_mm` null. `rest_mm` darf negativ werden (mehr verbraucht als erfasst),
 * `frei_mm` = `rest_mm` − vorgemerkte (wartende) Aufträge.
 */
export interface PlotRollenStand {
    jetzt: string;
    company_id: string | null;
    darf_erfassen: boolean;
    standard_laenge_mm: number;
    rolle: PlotRolle | null;
    verbraucht_mm: number;
    vorgemerkt_mm: number;
    rest_mm: number | null;
    frei_mm: number | null;
}

/** Eine Zeile aus `plot_druck_nachtraege`. */
export interface PlotDruckNachtrag {
    id: string;
    company_id: string;
    project_id: string | null;
    sige_projekt_id: string | null;
    titel: string;
    laenge_mm: number;
    kopien: number;
    gedruckt_am: string;
    bemerkung: string | null;
    erstellt_von: string;
    erstellt_von_name: string | null;
    erstellt_am: string;
}

/** Spaltenliste für das Lesen der Nachträge (PostgREST `select`). */
export const PLOT_NACHTRAG_SPALTEN =
    'id,company_id,project_id,sige_projekt_id,titel,laenge_mm,kopien,gedruckt_am,bemerkung,'
    + 'erstellt_von,erstellt_von_name,erstellt_am';

export interface PlotNachtragEingabe {
    /** Einmal je Absicht (crypto.randomUUID), ein zweiter Klick schickt dieselbe. */
    id: string;
    projectId: string;
    sigeProjektId: string | null;
    titel: string;
    laengeMm: number;
    kopien: number;
    /** 'YYYY-MM-DD' */
    datum: string;
    bemerkung?: string | null;
}

export type PlotDruckQuelle = 'app' | 'nachtrag';
export type PlotDruckZeilenStatus = 'gedruckt' | 'fehler' | 'druckt' | 'unklar' | 'nachgetragen';

const DRUCK_STATUS_WERTE: readonly PlotDruckZeilenStatus[] = ['gedruckt', 'fehler', 'druckt', 'unklar', 'nachgetragen'];

/** Eine Zeile der Druckliste (`plot_druck_uebersicht`), App-Auftrag oder Nachtrag. */
export interface PlotDruckZeile {
    quelle: PlotDruckQuelle;
    id: string;
    zeitpunkt: string;
    project_id: string | null;
    projekt_nummer: string | null;
    projekt_name: string | null;
    titel: string;
    /** Nur App-Aufträge; Nachträge kennen keine Breite. */
    breite_mm: number | null;
    laenge_mm: number;
    /** Bestellt. */
    kopien: number;
    /** Gegen die Rolle gezählt (Zählregeln im Vertrag / `plot_rolle_rechnen`). */
    kopien_gezaehlt: number;
    gesamt_mm: number;
    status: PlotDruckZeilenStatus;
    von_name: string | null;
    bemerkung: string | null;
    rolle_id: string | null;
}

export interface PlotRolleMitVerbrauch extends PlotRolle {
    /** `eingelegt_am` der nächsten Rolle; null = die aktuelle. */
    bis: string | null;
    verbraucht_mm: number;
}

export interface PlotDruckUebersicht {
    jetzt: string;
    company_id: string | null;
    stand: PlotRollenStand;
    rollen: PlotRolleMitVerbrauch[];
    drucke: PlotDruckZeile[];
    summe_mm: number;
    gekappt: boolean;
}

// ═══ Rein (ohne Datenbank) ═════════════════════════════════════════════════

/**
 * Rundet dezimal genau wie PostgreSQL `round(numeric, n)` bei positiven
 * Zahlen: über die Exponentenschreibweise statt `x * 10`, sonst würde
 * 841.15 zu 841.1 (Fließkomma 8411.499…). Fällt auf den einfachen Weg zurück,
 * wenn die Zahl selbst in Exponentenschreibweise steht.
 */
const runde = (v: number, stellen: number): number => {
    const genau = Number(`${Math.round(Number(`${v}e${stellen}`))}e-${stellen}`);
    if (Number.isFinite(genau)) return genau;
    const f = 10 ** stellen;
    return Math.round(v * f) / f;
};

/** Blattlänge wie in der Spalte `plot_auftraege.hoehe_mm` (numeric(7,1)); ungültig → 0. */
const hoeheLesen = (hoeheMm: number): number =>
    typeof hoeheMm === 'number' && Number.isFinite(hoeheMm) && hoeheMm > 0 ? runde(hoeheMm, 1) : 0;

/**
 * Ganze Kopien, mindestens eine — wie `kopienBegrenzen` beim Senden: es geht
 * nie ein Auftrag mit weniger als einer Kopie raus. Würde hier 0 gerechnet,
 * sagte das Urteil „reicht“ und der Server sperrte trotzdem. Nach oben wird
 * NICHT begrenzt (der Nachtrag erlaubt mehr Kopien als das Senden).
 */
const kopienLesen = (kopien: number): number => {
    if (typeof kopien !== 'number' || !Number.isFinite(kopien)) return 1;
    return Math.max(1, Math.floor(kopien));
};

/** Papierbedarf eines Drucks: Blattlänge × Kopien, auf 0,1 mm (wie der Trigger `hoehe_mm × kopien`). */
export const plotBedarfMm = (hoeheMm: number, kopien: number): number =>
    runde(hoeheLesen(hoeheMm) * kopienLesen(kopien), 1);

/**
 * Wie viele Kopien passen noch? Erst geschätzt, dann an der Grenze mit
 * demselben Vergleich wie im Urteil um eins korrigiert (Fließkomma: 2523,9 /
 * 841,3 ergibt nicht immer genau 3).
 */
const maxKopienBei = (frei: number, hoeheMm: number, kopien: number): number => {
    const h = hoeheLesen(hoeheMm);
    const obergrenze = kopienLesen(kopien) - 1;
    if (h <= 0 || frei <= 0 || obergrenze <= 0) return 0;
    const passt = (k: number): boolean => runde(h * k, 1) <= frei;
    let k = Math.floor(frei / h);
    if (k > 0 && !passt(k)) k -= 1;
    else if (passt(k + 1)) k += 1;
    return Math.max(0, Math.min(k, obergrenze));
};

export type PlotRollenUrteil =
    /** Stand null, SQL fehlt oder keine Rolle erfasst → nie sperren. */
    | { art: 'ohne_rolle' }
    /** `restNachherMm` = frei − bedarf; `knapp` = restNachherMm < PLOT_ROLLE_KNAPP_MM. */
    | { art: 'reicht'; bedarfMm: number; restNachherMm: number; knapp: boolean }
    /**
     * `freiMm` roh vom Server, darf negativ sein (die Anzeige entscheidet, ob
     * sie 0 zeigt). `maxKopien` = floor(max(frei, 0) / hoehe), höchstens kopien − 1.
     */
    | { art: 'reicht_nicht'; bedarfMm: number; freiMm: number; maxKopien: number };

/**
 * Reicht die eingelegte Rolle für diesen Druck? Derselbe Vergleich wie im
 * Trigger: gesperrt wird bei `frei_mm < bedarf` — genau gleich reicht also.
 *
 * Maßgeblich ist `frei_mm`: nur er wird verglichen. Liefert der Server ihn
 * nicht (keine Rolle, SQL fehlt), gibt es kein Urteil → `ohne_rolle`. Eine
 * Rolle, die sich nicht lesen ließ, bei vorhandenem `frei_mm` wird trotzdem
 * beurteilt — der Server hat eine Rolle und sperrte ebenso.
 *
 * Ungültige Blatthöhe (0, NaN): Bedarf 0. Dann bleibt nur die Frage, ob die
 * Rolle schon überzogen ist (frei < 0) — dann sperrte der Server jeden Druck.
 */
export const rollenUrteil = (stand: PlotRollenStand | null, hoeheMm: number, kopien: number): PlotRollenUrteil => {
    const frei = stand?.frei_mm;
    if (!stand || typeof frei !== 'number' || !Number.isFinite(frei)) return { art: 'ohne_rolle' };
    const bedarfMm = plotBedarfMm(hoeheMm, kopien);
    if (frei >= bedarfMm) {
        const restNachherMm = runde(frei - bedarfMm, 1);
        return { art: 'reicht', bedarfMm, restNachherMm, knapp: restNachherMm < PLOT_ROLLE_KNAPP_MM };
    }
    return { art: 'reicht_nicht', bedarfMm, freiMm: frei, maxKopien: maxKopienBei(frei, hoeheMm, kopien) };
};

const METER_FORMAT = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * „1,00 m“, „12,35 m“, „−0,40 m“ — de-DE, immer 2 Nachkommastellen.
 * Negativ mit echtem Minuszeichen (U+2212); was auf 0,00 rundet, hat kein
 * Vorzeichen. Keine Zahl (NaN, ∞) → „–“.
 */
export const meterText = (mm: number): string => {
    if (typeof mm !== 'number' || !Number.isFinite(mm)) return '–';
    const zentimeter = Math.round(Math.abs(mm) / 10);
    const betrag = METER_FORMAT.format(zentimeter / 100);
    return zentimeter > 0 && mm < 0 ? `−${betrag} m` : `${betrag} m`;
};

/** Erste Gruppe ohne führende Null, dann Dreiergruppen. */
const gruppiert = (t: string, trenner: '.' | ','): boolean =>
    new RegExp(`^[1-9]\\d{0,2}(\\${trenner}\\d{3})+$`).test(t);

/**
 * Zahlteil → JS-lesbare Dezimalzahl ohne Gruppierung („1200.5“), sonst null.
 *
 * Regeln:
 *  - Komma UND Punkt: das letzte Zeichen von beiden trennt die Dezimalen, das
 *    andere gruppiert in Dreiergruppen („1.200,5“ und „1,200.5“ → 1200,5).
 *  - nur EIN Komma: Dezimalkomma („1,2“; auch „1,200“ = 1,2).
 *  - nur EIN Punkt: Dezimalpunkt („1.2“) — außer bei cm/mm und genau einer
 *    Dreiergruppe dahinter („1.200 mm“ → 1200 mm): drei Nachkommastellen in
 *    cm oder mm wären Bruchteile eines Zehntelmillimeters, die ohnehin
 *    weggerundet würden; gemeint ist dort der Tausenderpunkt. In Metern
 *    (und ohne Einheit) bleibt der Punkt immer Dezimalpunkt: Rollen- und
 *    Planlängen liegen weit unter 1000 m, „1.200“ heißt dort 1,2 m.
 *  - mehrere gleiche Trenner: nur als gültige Dreiergruppen („1.200.000“).
 */
const zahlteilLesen = (t: string, tausenderPunkt: boolean): string | null => {
    if (!/\d/.test(t)) return null;
    const komma = (t.match(/,/g) ?? []).length;
    const punkt = (t.match(/\./g) ?? []).length;

    const dezimal = (ganz: string, bruch: string): string | null => {
        if (!/^\d*$/.test(ganz) || !/^\d*$/.test(bruch)) return null;
        return `${ganz || '0'}${bruch ? `.${bruch}` : ''}`;
    };

    if (komma > 0 && punkt > 0) {
        const dezTrenner = t.lastIndexOf(',') > t.lastIndexOf('.') ? ',' : '.';
        const gruppe: '.' | ',' = dezTrenner === ',' ? '.' : ',';
        const stelle = t.lastIndexOf(dezTrenner);
        const ganz = t.slice(0, stelle);
        const bruch = t.slice(stelle + 1);
        if (ganz.includes(dezTrenner) || !gruppiert(ganz, gruppe)) return null;
        return dezimal(ganz.split(gruppe).join(''), bruch);
    }
    if (komma === 0 && punkt === 0) return dezimal(t, '');
    const trenner: '.' | ',' = komma > 0 ? ',' : '.';
    const anzahl = komma > 0 ? komma : punkt;
    if (anzahl > 1) return gruppiert(t, trenner) ? t.split(trenner).join('') : null;
    if (trenner === '.' && tausenderPunkt && gruppiert(t, '.')) return t.replace('.', '');
    const [ganz, bruch] = t.split(trenner);
    return dezimal(ganz, bruch);
};

const EINHEIT_EXPONENT: Record<'m' | 'cm' | 'mm', number> = { m: 3, cm: 1, mm: 0 };

/**
 * Eingabe → mm (ganze mm). Ohne Einheit = Meter: „1,2“ → 1200; „120 cm“ → 1200;
 * „1200 mm“ → 1200; „1.2 m“ → 1200; „1.200 mm“ → 1200; ungültig, ≤ 0 → null.
 * Leerzeichen (auch geschützte) sind egal, Einheiten groß oder klein.
 * Trennzeichen-Regeln siehe `zahlteilLesen`. Gerundet wird halb aufwärts.
 */
export const laengeParsen = (eingabe: string): number | null => {
    if (typeof eingabe !== 'string') return null;
    const m = /^([0-9.,]+)(mm|cm|m)?$/.exec(eingabe.replace(/\s+/g, '').toLowerCase());
    if (!m) return null;
    const einheit = (m[2] ?? 'm') as 'm' | 'cm' | 'mm';
    const zahlteil = zahlteilLesen(m[1], einheit !== 'm');
    if (zahlteil === null) return null;
    // Dezimal genau: „1,0005 m“ ist 1000,5 mm, nicht 1000,4999…
    const mm = Number(`${zahlteil}e${EINHEIT_EXPONENT[einheit]}`);
    if (!Number.isFinite(mm)) return null;
    const ganz = Math.round(mm);
    return ganz > 0 ? ganz : null;
};

const BERLIN_TAG = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

/** Heutiges Datum in Europe/Berlin als 'YYYY-MM-DD'. Ungültiges `jetzt` → die Geräteuhr. */
export const heuteBerlin = (jetzt?: Date): string => {
    const zeit = jetzt instanceof Date && Number.isFinite(jetzt.getTime()) ? jetzt : new Date();
    const teile = BERLIN_TAG.formatToParts(zeit);
    const wert = (typ: Intl.DateTimeFormatPartTypes): string => teile.find((t) => t.type === typ)?.value ?? '';
    return `${wert('year')}-${wert('month')}-${wert('day')}`;
};

// ═══ Normalisieren ═════════════════════════════════════════════════════════

const wahr = (v: unknown): boolean => v === true || v === 'true';

/** jsonb-Array; ein JSON-Text wird gelesen, alles andere ist leer. */
const alsListe = (v: unknown): unknown[] => {
    let w = v;
    if (typeof w === 'string') {
        try {
            w = JSON.parse(w);
        } catch {
            return [];
        }
    }
    return Array.isArray(w) ? w : [];
};

/**
 * Unbekannte `art` (neuere Serverfassung) wird `neu`, nicht `korrektur`: für
 * die Zählung sind beide gleich — jede Zeile in `plot_rollen` startet die
 * Zählung mit `laenge_mm` neu. `korrektur` behauptete zusätzlich, jemand habe
 * den Rest von Hand nachgemessen; `neu` sagt nur „ab hier mit dieser Länge“.
 * Die Zeile wegzulassen wäre schlechter: dann fehlte die maßgebliche Rolle,
 * und die Anzeige widerspräche der Sperre des Servers.
 */
const rolleArtLesen = (v: unknown): PlotRolleArt => (text(v).trim().toLowerCase() === 'korrektur' ? 'korrektur' : 'neu');

const rolleLesen = (z: Zeile): PlotRolle | null => {
    const id = textOderNull(z.id);
    if (!id) return null;
    return {
        id,
        art: rolleArtLesen(z.art),
        laenge_mm: zahl(z.laenge_mm, 0),
        eingelegt_am: text(z.eingelegt_am).trim(),
        erstellt_von_name: textOderNull(z.erstellt_von_name),
        bemerkung: textOderNull(z.bemerkung),
    };
};

const rolleNormalisieren = (roh: unknown): PlotRolle | null => {
    const z = alsObjekt(roh);
    return z ? rolleLesen(z) : null;
};

const rolleMitVerbrauchNormalisieren = (roh: unknown): PlotRolleMitVerbrauch | null => {
    const z = alsObjekt(roh);
    const rolle = z ? rolleLesen(z) : null;
    if (!z || !rolle) return null;
    return { ...rolle, bis: textOderNull(z.bis), verbraucht_mm: zahl(z.verbraucht_mm, 0) };
};

/** Antwort von `plot_rolle_stand()` (oder `stand` der Übersicht) robust lesen. */
export const rollenStandNormalisieren = (roh: unknown): PlotRollenStand => {
    const z = alsObjekt(roh) ?? {};
    const standard = zahlOderNull(z.standard_laenge_mm);
    return {
        jetzt: text(z.jetzt).trim(),
        company_id: textOderNull(z.company_id),
        darf_erfassen: wahr(z.darf_erfassen),
        standard_laenge_mm: standard !== null && standard > 0 ? standard : PLOT_ROLLE_STANDARD_MM,
        rolle: rolleNormalisieren(z.rolle),
        verbraucht_mm: zahl(z.verbraucht_mm, 0),
        vorgemerkt_mm: zahl(z.vorgemerkt_mm, 0),
        rest_mm: zahlOderNull(z.rest_mm),
        frei_mm: zahlOderNull(z.frei_mm),
    };
};

export const nachtragNormalisieren = (roh: unknown): PlotDruckNachtrag | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = textOderNull(z.id);
    if (!id) return null;
    return {
        id,
        company_id: text(z.company_id).trim(),
        project_id: textOderNull(z.project_id),
        sige_projekt_id: textOderNull(z.sige_projekt_id),
        titel: text(z.titel),
        laenge_mm: zahl(z.laenge_mm, 0),
        kopien: Math.max(1, Math.floor(zahl(z.kopien, 1))),
        gedruckt_am: text(z.gedruckt_am).trim(),
        bemerkung: textOderNull(z.bemerkung),
        erstellt_von: text(z.erstellt_von).trim(),
        erstellt_von_name: textOderNull(z.erstellt_von_name),
        erstellt_am: text(z.erstellt_am).trim(),
    };
};

/**
 * Unbekannte `quelle` wird `app`: an `nachtrag` hängt in der Oberfläche das
 * Löschen (`plot_druck_nachtrag_loeschen`) — eine fremde Zeile soll das nie
 * anbieten. Unbekannter Status wird `unklar` (wie bei den Aufträgen): der
 * Client weiß dann nicht, was mit dem Druck ist.
 */
const druckZeileNormalisieren = (roh: unknown): PlotDruckZeile | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = textOderNull(z.id);
    if (!id) return null;
    const statusRoh = text(z.status).trim().toLowerCase();
    const laenge = zahl(z.laenge_mm, 0);
    const gezaehlt = Math.max(0, Math.floor(zahl(z.kopien_gezaehlt, 0)));
    return {
        quelle: text(z.quelle).trim().toLowerCase() === 'nachtrag' ? 'nachtrag' : 'app',
        id,
        zeitpunkt: text(z.zeitpunkt).trim(),
        project_id: textOderNull(z.project_id),
        projekt_nummer: textOderNull(z.projekt_nummer),
        projekt_name: textOderNull(z.projekt_name),
        titel: text(z.titel),
        breite_mm: zahlOderNull(z.breite_mm),
        laenge_mm: laenge,
        kopien: Math.max(0, Math.floor(zahl(z.kopien, 0))),
        kopien_gezaehlt: gezaehlt,
        gesamt_mm: zahlOderNull(z.gesamt_mm) ?? runde(laenge * gezaehlt, 1),
        status: (DRUCK_STATUS_WERTE as readonly string[]).includes(statusRoh) ? (statusRoh as PlotDruckZeilenStatus) : 'unklar',
        von_name: textOderNull(z.von_name),
        bemerkung: textOderNull(z.bemerkung),
        rolle_id: textOderNull(z.rolle_id),
    };
};

/**
 * Antwort von `plot_druck_uebersicht` robust lesen. `stand` kommt dort ohne
 * `jetzt`/`company_id` — die werden von oben übernommen. Fehlt `summe_mm`,
 * gilt die Summe der gelieferten Zeilen.
 */
export const druckUebersichtNormalisieren = (roh: unknown): PlotDruckUebersicht => {
    const z = alsObjekt(roh) ?? {};
    const standRoh = alsObjekt(z.stand) ?? {};
    const stand = rollenStandNormalisieren({
        ...standRoh,
        jetzt: standRoh.jetzt ?? z.jetzt,
        company_id: standRoh.company_id ?? z.company_id,
    });
    const rollen = alsListe(z.rollen)
        .map(rolleMitVerbrauchNormalisieren)
        .filter((r): r is PlotRolleMitVerbrauch => r !== null);
    const drucke = alsListe(z.drucke)
        .map(druckZeileNormalisieren)
        .filter((d): d is PlotDruckZeile => d !== null);
    return {
        jetzt: text(z.jetzt).trim(),
        company_id: textOderNull(z.company_id),
        stand,
        rollen,
        drucke,
        summe_mm: zahlOderNull(z.summe_mm) ?? runde(drucke.reduce((s, d) => s + d.gesamt_mm, 0), 1),
        gekappt: wahr(z.gekappt),
    };
};

// ═══ Datenbank ═════════════════════════════════════════════════════════════

const TABELLE_NACHTRAEGE = 'plot_druck_nachtraege';

const TEXT_STAND = 'Der Stand der Papierrolle ließ sich nicht laden.';
const TEXT_ERFASSEN = 'Die Papierrolle ließ sich nicht speichern.';
const TEXT_NACHTRAGEN = 'Der Druck ließ sich nicht nachtragen.';
const TEXT_LOESCHEN = 'Der nachgetragene Druck ließ sich nicht löschen.';
const TEXT_LISTE = 'Die Druckliste ließ sich nicht laden.';

const nichtEingerichtet = (e: unknown): boolean => e instanceof PlotFehler && e.code === 'nicht_eingerichtet';

/** Zeichen wie PostgreSQL `length(text)` zählt (Codepunkte, nicht UTF-16-Einheiten). */
const zeichen = (s: string): number => [...s].length;

const bemerkungLesen = (roh: string | null | undefined): string | null => {
    const b = leerZuNull(roh);
    if (b !== null && zeichen(b) > BEMERKUNG_MAX) {
        throw fehler('angabe', `Die Bemerkung darf höchstens ${BEMERKUNG_MAX} Zeichen haben.`);
    }
    return b;
};

const nachtragsId = (roh: string): string => {
    const id = (typeof roh === 'string' ? roh : '').trim().toLowerCase();
    if (!UUID.test(id)) throw fehler('unbekannt', 'Die ID des Nachtrags ist ungültig — bitte den Dialog schließen und neu öffnen.');
    return id;
};

/** Ein echter Kalendertag 'YYYY-MM-DD' (kein 31.02.). */
const istKalendertag = (t: string): boolean => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (!m) return false;
    const [j, mo, ta] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(Date.UTC(j, mo - 1, ta));
    return d.getUTCFullYear() === j && d.getUTCMonth() === mo - 1 && d.getUTCDate() === ta;
};

/**
 * Stand der eingelegten Rolle der eigenen Firma. `null` = SQL Nr. 169/170
 * fehlt in dieser Umgebung — dann wird nie gesperrt. Sonst wirft es nur bei
 * echten Fehlern.
 */
export async function plotRollenStandLaden(): Promise<PlotRollenStand | null> {
    try {
        return rollenStandNormalisieren(await rufe('plot_rolle_stand', undefined, TEXT_STAND));
    } catch (e) {
        if (nichtEingerichtet(e)) return null;
        throw e;
    }
}

/**
 * Nur Admins: neue Rolle eingelegt (`neu`, volle Länge) oder gemessene
 * Restlänge (`korrektur`). Ab dieser Zeile zählt der Server neu. Die Länge
 * geht auf ganze mm gerundet raus; die Bereiche prüft der Server ebenso
 * (`PLOT_ROLLE_ANGABE`) — hier nur, damit der Fehler ohne Netz kommt.
 */
export async function plotRolleErfassen(art: PlotRolleArt, laengeMm: number, bemerkung?: string | null): Promise<PlotRollenStand> {
    if (art !== 'neu' && art !== 'korrektur') {
        throw fehler('angabe', 'Unbekannte Art der Rollenerfassung — bitte „Neue Rolle“ oder „Restlänge korrigieren“ wählen.');
    }
    const laenge = typeof laengeMm === 'number' && Number.isFinite(laengeMm) ? Math.round(laengeMm) : Number.NaN;
    const min = art === 'neu' ? PLOT_ROLLE_MIN_NEU_MM : 0;
    if (!(laenge >= min && laenge <= PLOT_ROLLE_MAX_MM)) {
        throw fehler('angabe', art === 'neu'
            ? `Die Rollenlänge muss zwischen ${PLOT_ROLLE_MIN_NEU_MM / 1000} und ${PLOT_ROLLE_MAX_MM / 1000} m liegen.`
            : `Die Restlänge muss zwischen 0 und ${PLOT_ROLLE_MAX_MM / 1000} m liegen.`);
    }
    const daten = await rufe('plot_rolle_erfassen', {
        p_art: art,
        p_laenge_mm: laenge,
        p_bemerkung: bemerkungLesen(bemerkung),
    }, TEXT_ERFASSEN);
    if (!alsObjekt(daten)) {
        throw fehler('unbekannt', 'Der Server hat die Rolle nicht bestätigt — bitte den Stand neu laden.');
    }
    return rollenStandNormalisieren(daten);
}

/**
 * Druck von Hand nachtragen (alter Plan direkt vom Rechner gedruckt).
 * Idempotent über `e.id`: ein zweiter Klick bekommt die bestehende Zeile.
 *
 * Länge (0,1 mm), Kopien, Titel und Bemerkung werden hier schon geprüft —
 * mit denselben Grenzen und Sätzen wie im SQL. Kopien werden NICHT begrenzt:
 * ein Nachtrag hält fest, was gedruckt wurde, und darf nicht still eine
 * andere Zahl buchen. Das Datum wird nur auf die Form geprüft; „nicht in der
 * Zukunft, höchstens 366 Tage zurück“ entscheidet der Server mit seiner Uhr
 * (die Geräteuhr kann falsch gehen). Leeres Datum → null → der Server nimmt
 * heute (Europe/Berlin).
 */
export async function plotDruckNachtragen(e: PlotNachtragEingabe): Promise<PlotDruckNachtrag> {
    const id = nachtragsId(e?.id);
    const projekt = leerZuNull(e.projectId);
    if (!projekt) throw fehler('angabe', 'Der Nachtrag braucht ein Projekt.');

    const laenge = typeof e.laengeMm === 'number' && Number.isFinite(e.laengeMm) ? runde(e.laengeMm, 1) : Number.NaN;
    if (!(laenge >= PLOT_NACHTRAG_MIN_MM && laenge <= PLOT_NACHTRAG_MAX_MM)) {
        throw fehler('angabe', `Die Länge muss zwischen ${meterText(PLOT_NACHTRAG_MIN_MM)} und ${meterText(PLOT_NACHTRAG_MAX_MM)} liegen.`);
    }
    const kopien = e.kopien;
    if (typeof kopien !== 'number' || !Number.isInteger(kopien) || kopien < 1 || kopien > PLOT_NACHTRAG_MAX_KOPIEN) {
        throw fehler('angabe', `Die Anzahl der Kopien muss zwischen 1 und ${PLOT_NACHTRAG_MAX_KOPIEN} liegen.`);
    }
    const datum = leerZuNull(e.datum);
    if (datum !== null && !istKalendertag(datum)) {
        throw fehler('angabe', 'Bitte ein gültiges Druckdatum angeben.');
    }
    const titel = (typeof e.titel === 'string' ? e.titel : '').trim();
    if (zeichen(titel) > TITEL_MAX) {
        throw fehler('angabe', `Der Titel darf höchstens ${TITEL_MAX} Zeichen haben.`);
    }

    const daten = await rufe('plot_druck_nachtragen', {
        p_id: id,
        p_project_id: projekt,
        p_sige_projekt_id: leerZuNull(e.sigeProjektId),
        p_titel: titel,
        p_laenge_mm: laenge,
        p_kopien: kopien,
        p_datum: datum,
        p_bemerkung: bemerkungLesen(e.bemerkung),
    }, TEXT_NACHTRAGEN);
    const n = nachtragNormalisieren(daten);
    if (!n) throw fehler('unbekannt', 'Der Server hat den Nachtrag nicht bestätigt — bitte die Liste neu laden.');
    return n;
}

/** Nur Ersteller oder Admin derselben Firma (sonst `kein_zugriff`). */
export async function plotDruckNachtragLoeschen(id: string): Promise<void> {
    await rufe('plot_druck_nachtrag_loeschen', { p_id: nachtragsId(id) }, TEXT_LOESCHEN);
}

const NACHTRAEGE_STANDARD = 20;
const NACHTRAEGE_MAX = 200;

/**
 * Nachträge eines Projekts, neueste zuerst. Bei gleichem `gedruckt_am`
 * (frühere Tage stehen alle auf 12:00) entscheidet `erstellt_am`. Ohne
 * Projekt-ID keine Abfrage — ungefiltert kämen alle der Firma. Fehlt die
 * Tabelle (SQL Nr. 169 offen), ist die Liste leer.
 */
export async function plotDruckNachtraegeLaden(filter: { projectId: string; limit?: number }): Promise<PlotDruckNachtrag[]> {
    const projekt = leerZuNull(filter?.projectId);
    if (!projekt) return [];
    const roh = filter?.limit;
    const limit = typeof roh === 'number' && Number.isFinite(roh)
        ? Math.min(NACHTRAEGE_MAX, Math.max(1, Math.floor(roh)))
        : NACHTRAEGE_STANDARD;

    let antwort: Antwort;
    try {
        antwort = alsAntwort(await supabase
            .from(TABELLE_NACHTRAEGE)
            .select(PLOT_NACHTRAG_SPALTEN)
            .eq('project_id', projekt)
            .order('gedruckt_am', { ascending: false })
            .order('erstellt_am', { ascending: false })
            .limit(limit));
    } catch (e) {
        throw alsPlotFehler(e, undefined, TEXT_LISTE);
    }
    if (antwort.error) {
        const f = alsPlotFehler(antwort.error, antwort.status, TEXT_LISTE);
        if (f.code === 'nicht_eingerichtet') return [];
        throw f;
    }
    return alsListe(antwort.data)
        .map(nachtragNormalisieren)
        .filter((n): n is PlotDruckNachtrag => n !== null);
}

/**
 * Druckliste für den Admin (`nur_admin` sonst). `von`/`bis` als Zeitpunkt
 * (ISO), `bis` exklusiv, null = offen. `null` = SQL Nr. 169/170 fehlt.
 */
export async function plotDruckUebersichtLaden(von: string | null, bis: string | null): Promise<PlotDruckUebersicht | null> {
    try {
        return druckUebersichtNormalisieren(await rufe('plot_druck_uebersicht', {
            p_von: leerZuNull(von),
            p_bis: leerZuNull(bis),
        }, TEXT_LISTE));
    } catch (e) {
        if (nichtEingerichtet(e)) return null;
        throw e;
    }
}
